/* eslint-disable max-len */
import React, { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

export type VpStickySectionNavItem = {
    id: string;
    label: string;
};

type VpStickySectionNavProps = {
    items: VpStickySectionNavItem[];
    /** Color scheme: "primary" uses theme primary, "accent" uses edition-specific accent color. Default: "primary". */
    colorScheme?: "accent" | "primary";
};

export const VpStickySectionNav: React.FC<VpStickySectionNavProps> = ({ items, colorScheme = "primary" }) => {
    const [activeSection, setActiveSection] = useState<string>("");
    const [isSticky, setIsSticky] = useState(false);
    const [isMobileOpen, setIsMobileOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const navRef = useRef<HTMLElement>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (items.length === 0) {
            return;
        }

        const handleScroll = () => {
            if (wrapperRef.current) {
                const wrapperTop = wrapperRef.current.getBoundingClientRect().top;
                setIsSticky(wrapperTop <= 0);
            }

            const sections = items.map(item => ({
                id: item.id,
                element: document.getElementById(item.id),
            }));

            let currentActive = "";
            const navHeight = navRef.current?.offsetHeight ?? 60;
            const scrollPosition = window.scrollY + navHeight + 40;

            for (let i = sections.length - 1; i >= 0; i--) {
                const section = sections[i];
                if (!section.element) {
                    continue;
                }

                const sectionTop = section.element.offsetTop;
                const nextSection = sections[i + 1];
                const nextSectionTop = nextSection?.element ? nextSection.element.offsetTop : Infinity;

                if (scrollPosition >= sectionTop && scrollPosition < nextSectionTop) {
                    currentActive = section.id;
                    break;
                }
            }

            setActiveSection(currentActive);
        };

        window.addEventListener("scroll", handleScroll);
        handleScroll();

        return () => window.removeEventListener("scroll", handleScroll);
    }, [items]);

    useEffect(() => {
        if (!activeSection || !scrollContainerRef.current) {
            return;
        }
        const container = scrollContainerRef.current;
        const activeEl = container.querySelector<HTMLElement>(`[data-section="${activeSection}"]`);
        if (activeEl) {
            const scrollLeft = activeEl.offsetLeft - container.offsetWidth / 2 + activeEl.offsetWidth / 2;
            container.scrollTo({ left: scrollLeft, behavior: "smooth" });
        }
    }, [activeSection]);

    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
        e.preventDefault();
        setIsMobileOpen(false);
        const element = document.getElementById(id);
        if (element) {
            const navHeight = navRef.current?.offsetHeight ?? 60;
            const offset = navHeight + 20;
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - offset;

            window.scrollTo({
                top: offsetPosition,
                behavior: "smooth",
            });
        }
    };

    if (items.length === 0) {
        return null;
    }

    const activeLabel = items.find(item => item.id === activeSection)?.label ?? "Navigation";

    const navHeight = navRef.current?.offsetHeight ?? 0;

    const activeClass =
        colorScheme === "primary" ? "bg-white text-primary shadow-sm" : "bg-white text-accent shadow-sm";
    const inactiveClass =
        colorScheme === "primary"
            ? "text-white/80 hover:bg-white/15 hover:text-white"
            : "text-white/80 hover:bg-white/15 hover:text-white";
    const mobileActiveClass =
        colorScheme === "primary" ? "bg-white/20 text-white font-semibold" : "bg-white/20 text-white font-semibold";
    const mobileInactiveClass =
        colorScheme === "primary"
            ? "text-white/80 hover:bg-white/10 active:bg-white/15"
            : "text-white/80 hover:bg-white/10 active:bg-white/15";
    const ringClass = colorScheme === "primary" ? "focus-visible:ring-white" : "focus-visible:ring-white";

    return (
        <div ref={wrapperRef} style={isSticky ? { height: navHeight } : undefined}>
            <nav
                ref={navRef}
                className={`${
                    isSticky ? "fixed top-0 left-0 right-0 shadow-sm" : "relative"
                } z-[60] bg-primary/90 backdrop-blur-md border-b border-white/10 transition-all duration-200`}
            >
                <div className="mx-auto w-full px-4 md:px-12">
                    {/* Desktop navigation */}
                    <div className="hidden md:flex items-center justify-center">
                        <div
                            ref={scrollContainerRef}
                            className="flex items-center gap-1 lg:gap-2 py-3 overflow-x-auto no-scrollbar"
                        >
                            {items.map(item => (
                                <a
                                    key={item.id}
                                    data-section={item.id}
                                    href={`#${item.id}`}
                                    onClick={e => handleClick(e, item.id)}
                                    className={`px-3 lg:px-4 py-2 text-sm lg:text-base font-medium whitespace-nowrap rounded-full transition-[color,background-color,box-shadow] duration-200 focus-visible:outline-none focus-visible:ring-2 ${ringClass} focus-visible:ring-offset-2 ${
                                        activeSection === item.id ? activeClass : inactiveClass
                                    }`}
                                >
                                    {item.label}
                                </a>
                            ))}
                        </div>
                    </div>

                    {/* Mobile dropdown */}
                    <div className="md:hidden py-2">
                        <button
                            type="button"
                            onClick={() => setIsMobileOpen(!isMobileOpen)}
                            className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-white bg-white/15 rounded-lg"
                        >
                            <span>{activeLabel}</span>
                            <ChevronDown
                                className={`w-4 h-4 transition-transform duration-200 ${isMobileOpen ? "rotate-180" : ""}`}
                            />
                        </button>
                        {isMobileOpen && (
                            <div className="mt-1 bg-primary rounded-lg shadow-lg overflow-hidden">
                                {items.map(item => (
                                    <a
                                        key={item.id}
                                        href={`#${item.id}`}
                                        onClick={e => handleClick(e, item.id)}
                                        className={`block px-4 py-3 text-sm font-medium transition-[color,background-color] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset ${ringClass} ${
                                            activeSection === item.id ? mobileActiveClass : mobileInactiveClass
                                        }`}
                                    >
                                        {item.label}
                                    </a>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </nav>
        </div>
    );
};
